import axios, {AxiosInstance} from 'axios'
import MyAxiosInstanceFlow from './axiosInstanceFLow'
import {TemplateConfig} from '../types/templateConfig'
import {FlowDocumentType, ProjectType} from '../types/flowData'

export class FlowApi {
    private flowApi: AxiosInstance
    constructor() {
        this.flowApi = MyAxiosInstanceFlow
    }

    async getFlow(): Promise<any> {
        try {
            const res = await this.flowApi.get('/document-flows')
            return res.data
        } catch (error) {
            console.error('Erro ao obter fluxo de documentos:', error)
            throw error
        }
    }

    async getFlowById(flow_id: string): Promise<any> {
        try {
            const res = await this.flowApi.get(`/document-flows/${flow_id}`)
            return res.data
        } catch (error) {
            console.error('Erro ao obter fluxo de documentos por ID:', error)
            throw error
        }
    }

    async createFlow(flowData: any): Promise<any> {
        try {
            const res = await this.flowApi.post('/document-flows', flowData)
            return res.data
        } catch (error) {
            console.error('Erro ao criar fluxo de documentos:', error)
            throw error
        }
    }

    async updateFlow(flowData: any): Promise<any> {
        try {
            const res = await this.flowApi.put(`/document-flows`, flowData)
            return res.data
        } catch (error) {
            console.error('Erro ao atualizar fluxo de documentos:', error)
            throw error
        }
    }

    //Rotas novas do back

    async updateDocumentParameters(flow_id: string, documentParameters: any): Promise<any> {
        try {
            const res = await this.flowApi.put(`/document-flows/${flow_id}/document_parameters`, documentParameters)
            return res.data
        } catch (error) {
            console.error('Erro ao atualizar document parameters:', error)
            throw error
        }
    }

    async patchDocumentParameters(flow_id: string, documentParameters: any): Promise<any> {
        try {
            const res = await this.flowApi.patch(`/document-flows/${flow_id}/document_parameters`, documentParameters)
            return res.data
        } catch (error) {
            console.error('Erro ao atualizar document parameters (parcial):', error)
            throw error
        }
    }

    /** Cria um projeto a partir da minuta e dispara o try-get-answers. */
    async createDraftProject(
        flow_id: string,
        data: {
            organization_uuid: string
            document_type_id: string
            project_type_id: string
            document_key?: string | null
            name?: string | null
            // Instrução extra para a LLM que responde as perguntas do fluxo.
            answer_instruction?: string | null
            // 'draft' responde pela minuta no próprio backend; 'upload' só cria
            // o projeto e devolve question_id para o front enviar o arquivo.
            source?: 'draft' | 'upload'
        }
    ): Promise<{
        project_id: string
        question_id?: string | null
        dispatched: boolean
        reason?: string | null
        questions?: number | null
    }> {
        try {
            const res = await this.flowApi.post(`/document-flows/${flow_id}/draft-project`, data)
            return res.data
        } catch (error) {
            console.error('Erro ao gerar projeto a partir da minuta:', error)
            throw error
        }
    }

    /** Ajusta o draft.json: instrução em texto, um documento gerado, ou os dois.
     *
     * Com o documento, o backend compara minuta x manifesto x gerado e monta a
     * tabela de divergências que vai no prompt. Vai como multipart — por isso
     * não leva o wrapper {body} das outras rotas de document-flows.
     */
    async adjustManifest(
        flow_id: string,
        data: {
            organization_uuid: string
            document_type_id: string
            instruction?: string
            document_key?: string | null
            generatedDocument?: File | null
        }
    ): Promise<{dispatched: boolean; reason?: string | null}> {
        try {
            const form = new FormData()
            form.append('organization_uuid', data.organization_uuid)
            form.append('document_type_id', data.document_type_id)
            if (data.instruction) form.append('instruction', data.instruction)
            if (data.document_key) form.append('document_key', data.document_key)
            if (data.generatedDocument) {
                form.append('generated_document', data.generatedDocument)
            }
            const res = await this.flowApi.post(
                `/document-flows/${flow_id}/manifest-adjust`,
                form
            )
            return res.data
        } catch (error) {
            console.error('Erro ao ajustar manifesto:', error)
            throw error
        }
    }

    async getQuestionPreSignedUrl(data: {
    filename: string;
    question_uuid: string;
    project_uuid: string;
    }): Promise<{ url: string; tags: string }> {
        try {
            const res = await this.flowApi.post('/questions/pre-signed-url', data)
            return res.data
        } catch (error) {
            console.error('Erro ao obter URL pré-assinada para upload:', error)
            throw error
        }
    }

    async getUploadPreSignedUrl(
        data: {
            filename: 'draft.docx' | 'draft.txt' | 'template.docx';
            organization_id: string;
            document_type_id?: string | null;
            document_key?: string | null;
        },
        file: File
    ): Promise<string> {
        try {
            const res = await this.flowApi.post('/document-flows/pre-signed-url', data)
            const url: string = res.data

            await axios.put(url, file, {
                headers: {
                    'Content-Type': file.type,
                },
            })

            return  url
        } catch (error) {
            console.error('Erro ao obter URL pré-assinada para upload:', error)
            throw error
        }
    }

    // template_configs (document-generator)

    async getTemplateConfigs(flow_id: string): Promise<TemplateConfig[]> {
        try {
            const res = await this.flowApi.get(`/document-flows/${flow_id}/template-configs`)
            return res.data
        } catch (error) {
            console.error('Erro ao obter template configs:', error)
            throw error
        }
    }

    async getTemplateConfigByName(flow_id: string, name: string): Promise<TemplateConfig> {
        try {
            const res = await this.flowApi.get(
                `/document-flows/${flow_id}/template-configs/${encodeURIComponent(name)}`
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter template config:', error)
            throw error
        }
    }

    async createTemplateConfig(flow_id: string, data: TemplateConfig): Promise<TemplateConfig> {
        try {
            const res = await this.flowApi.post(`/document-flows/${flow_id}/template-configs`, {body: data})
            return res.data
        } catch (error) {
            console.error('Erro ao criar template config:', error)
            throw error
        }
    }

    async updateTemplateConfig(flow_id: string, name: string, data: TemplateConfig): Promise<TemplateConfig> {
        try {
            const res = await this.flowApi.put(
                `/document-flows/${flow_id}/template-configs/${encodeURIComponent(name)}`,
                {body: data}
            )
            return res.data
        } catch (error) {
            console.error('Erro ao atualizar template config:', error)
            throw error
        }
    }

    async deleteTemplateConfig(flow_id: string, name: string): Promise<void> {
        try {
            await this.flowApi.delete(`/document-flows/${flow_id}/template-configs/${encodeURIComponent(name)}`)
        } catch (error) {
            console.error('Erro ao deletar template config:', error)
            throw error
        }
    }

    // project types x document types (document-generator)

    async getProjectTypes(): Promise<ProjectType[]> {
        try {
            const res = await this.flowApi.get('/document-flows/project-types')
            return res.data
        } catch (error) {
            console.error('Erro ao obter project types:', error)
            throw error
        }
    }

    // todos os document types do project type, sem olhar flow (tela admin de minutas)
    async getProjectTypeDocumentTypes(project_type_id: string): Promise<FlowDocumentType[]> {
        try {
            const res = await this.flowApi.get(`/document-flows/project-types/${project_type_id}/document-types`)
            return res.data
        } catch (error) {
            console.error('Erro ao obter document types do project type:', error)
            throw error
        }
    }

    // flow x organização (document-generator)

    async getFlowOrganizations(flow_id: string): Promise<any> {
        try {
            const res = await this.flowApi.get(`/document-flows/${flow_id}/organizations`)
            return res.data
        } catch (error) {
            console.error('Erro ao obter organizações do fluxo:', error)
            throw error
        }
    }

    async getAllOrganizations(): Promise<any> {
        try {
            const res = await this.flowApi.get('/organizations')
            return res.data
        } catch (error) {
            console.error('Erro ao obter organizações:', error)
            throw error
        }
    }

    async updateOrganizationFlow(
        organization_id: string,
        data: {document_flow_id: string | null; document_flow_active: boolean}
    ): Promise<any> {
        try {
            const res = await this.flowApi.put(`/organizations/${organization_id}/document-flow`, data)
            return res.data
        } catch (error) {
            console.error('Erro ao vincular fluxo à organização:', error)
            throw error
        }
    }

    // logo da organização (document-generator)

    async getLogoPreSignedUrl(organization_id: string, filename: 'logo.png' | 'logo.jpg'): Promise<string> {
        try {
            const res = await this.flowApi.post(`/organizations/${organization_id}/logo/pre-signed-url`, {filename})
            return res.data
        } catch (error) {
            console.error('Erro ao obter URL pré-assinada do logo:', error)
            throw error
        }
    }

    async confirmLogoUpload(organization_id: string, filename: 'logo.png' | 'logo.jpg'): Promise<any> {
        try {
            const res = await this.flowApi.put(`/organizations/${organization_id}/logo`, {filename})
            return res.data
        } catch (error) {
            console.error('Erro ao confirmar upload do logo:', error)
            throw error
        }
    }

    async uploadOrganizationLogo(organization_id: string, file: File): Promise<void> {
        const filename: 'logo.png' | 'logo.jpg' = file.type === 'image/jpeg' ? 'logo.jpg' : 'logo.png'
        const url = await this.getLogoPreSignedUrl(organization_id, filename)
        await axios.put(url, file, {headers: {'Content-Type': file.type}})
        await this.confirmLogoUpload(organization_id, filename)
    }
}
